import WavingHandIcon from '@/assets/icons/waving_hand.png';
import PoweredByGrowersLogo from '@/assets/logos/black/powered_by_growers.svg';
import { Button, HSpacer, Text, TextLink, VSpacer } from '@/components/DesignSystem';
import { BoxedImage } from '@/components/DesignSystem/BoxedImage/BoxedImage';
import { AppConfig } from '@/constants/AppConfig';
import { ApiRetailer, ApiUser } from '@api/interfaces';
import Email from '@mui/icons-material/Email';
import Phone from '@mui/icons-material/Phone';
import { Stack, useTheme } from '@mui/material';
import { GrowersContactInfo } from '@shared/enums';
import { formatPhoneNumber } from '@shared/utilities';
import { getAccountName } from '@shared/utilities/UserUtilities';
import { useTranslation } from 'react-i18next';

export const cardStyle = {
  bgcolor: 'white',
  borderRadius: '8px',
  boxShadow: '0px 6px 12px -6px rgba(24, 39, 75, 0.12), 0px 8px 24px -4px rgba(24, 39, 75, 0.08);',
  padding: '0px',
};
export const darkenedSectionStyle = {
  background: 'linear-gradient(0deg, rgba(0, 0, 0, 0.03) 0%, rgba(0, 0, 0, 0.03) 100%), #FFF',
};
export const buttonHoverStyle = {
  '&:hover': { bgcolor: 'transparent', borderColor: 'inherit' },
};
export const kpiCardWrapperStyle = {
  flexGrow: 1,
  flexShrink: 1,
  flexBasis: { xs: 'calc(50% - 8px)', sm: 0 },
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  '& > *': {
    height: '100%',
    width: '100%',
  },
  '& .MuiStack-root:first-of-type': {
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    '& > :first-of-type': {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
  },
};

export const primaryKpiCardWrapperStyle = {
  ...kpiCardWrapperStyle,
  flexBasis: { xs: '100%', sm: 0 },
};

export const defaultDashboardBrandColor = '#97743a';

export const getBrandColor = (retailer: ApiRetailer) =>
  retailer.brandColor ?? defaultDashboardBrandColor;

export const DashboardHeader = ({ retailer }: { retailer: ApiRetailer }) => {
  const campaignImageUrl = retailer.campaignImage && (
    `${AppConfig.staticImageHost}/${retailer.campaignImage}`
  );
  return (
    <Stack bgcolor={getBrandColor(retailer)} color="white" height="140px" p="16px">
      <Stack alignItems="center" height="64px" justifyContent="center">
        <Text category="label-medium" textAlign="center">
          {retailer.name.toUpperCase()}
        </Text>
      </Stack>
      {!!campaignImageUrl && (
        <Stack alignItems="center" height="76px">
          <BoxedImage height="80px" src={campaignImageUrl} width="142px" />
        </Stack>
      )}
    </Stack>
  );
};

interface DashboardProgramInfoProps {
  brandColor: string,
  dateRange: string,
  name: string,
  onHowItWorks?: () => void,
}

export const DashboardProgramInfo = ({
  brandColor,
  dateRange,
  name,
  onHowItWorks,
}: DashboardProgramInfoProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const howItWorksButtonStyle = {
    '&:hover': {
      backgroundColor: brandColor,
      opacity: 0.9,
    },
    backgroundColor: brandColor,
    borderRadius: '2px',
    color: theme.palette.common.white,
    lineHeight: 'normal',
    minHeight: '30px',
    minWidth: '100px',
    padding: '7px 8px',
    textWrap: 'auto',
  };
  return (
    <Stack direction="row" gap="8px" justifyContent="space-between"
      p="16px" sx={darkenedSectionStyle}>
      <Stack>
        <Text category="title-medium">
          {name}
        </Text>
        <Text category="body-small">
          {dateRange}
        </Text>
      </Stack>
      {!!onHowItWorks && (
        <Button
          onClick={onHowItWorks}
          sx={howItWorksButtonStyle}
          testID="dashboard-how-it-works-button"
          variant="contained"
        >
          {t($ => $.customerDashboard.howItWorks.title)}
        </Button>
      )}
    </Stack>
  );
};

export const FarmerGreeting = ({ farmer }: { farmer: ApiUser }) => {
  const { t } = useTranslation();
  return (
    <Text
      fontSize="18px"
      fontWeight={400}
      sx={{
        display: 'inline',
        wordBreak: 'break-word',
      }}
    >
      {t($ => $.customerDashboard.landing.greeting, { name: getAccountName(farmer) })}
      <img
        src={WavingHandIcon}
        style={{
          display: 'inline-block',
          height: '20px',
          marginLeft: '8px',
          transform: 'scaleX(-1)',
          verticalAlign: 'text-bottom',
          width: '20px',
        }}
      />
    </Text>
  );
};

interface DashboardViewButtonProps {
  brandColor: string,
  onClick: () => void,
  testID: string,
}

export const DashboardViewButton = ({ brandColor, onClick, testID }: DashboardViewButtonProps) => {
  const { t } = useTranslation();
  return (
    <Button
      onClick={onClick}
      sx={{
        background: 'none !important',
        border: `1px solid ${brandColor} !important`,
        color: brandColor,
      }}
      testID={testID}
      variant="outlined"
    >
      <Text category="label-large" sx={{ padding: '0px 8px' }}>
        {t($ => $.customers.actions.view)}
      </Text>
    </Button>
  );
};

export const DashboardDisclaimer = () => {
  const { t } = useTranslation();
  return (
    <Stack  p="16px">
      <Text category="title-medium" sx={{ color: '#141414' }}>
        {t($ => $.insights.aboutModal.disclaimers)}
      </Text>
      <VSpacer size="3" />
      <Text category="body-medium" sx={{ color: '#141414' }}>
        {t($ => $.products.promotionsModal.subtitle)}
      </Text>
      <VSpacer size="3" />
      <Text category="body-medium" sx={{ color: '#141414' }}>
        {t($ => $.customerDashboard.disclaimer)}
      </Text>
    </Stack>
  );
};

export const DashboardFooter = ({ brandColor }: { brandColor: string }) => {
  const { t } = useTranslation();
  return (
    <Stack alignItems="center" p="32px 16px 24px" sx={darkenedSectionStyle}>
      <img src={PoweredByGrowersLogo} style={{ height: '42px' }} />
      <VSpacer size="7" />
      <Text category="title-small">
        {t($ => $.app.support.needHelp)}{' '}
        {t($ => $.app.support.contactTitle, { name: 'Growers' })}.
      </Text>
      <Stack direction="row">
        <Button
          href={`tel:${GrowersContactInfo.supportPhone}`}
          sx={{ color: brandColor, ...buttonHoverStyle }}
          testID="dashboard-contact-telephone-button"
          variant="text"
        >
          <Phone sx={{ height: '18px', width: '18px' }} />
          <HSpacer size="2" />
          {formatPhoneNumber(GrowersContactInfo.supportPhone, 'paren')}
        </Button>
        <Button
          href={`mailto:${GrowersContactInfo.supportEmail}`}
          sx={{ color: brandColor, ...buttonHoverStyle }}
          testID="dashboard-contact-email-button"
          variant="text"
        >
          <Email sx={{ height: '18px', width: '18px' }} />
          <HSpacer size="2" />
          {GrowersContactInfo.supportEmail}
        </Button>
      </Stack>
      <VSpacer size="7" />
      <TextLink
        category="body-small"
        href={GrowersContactInfo.termsOfUseLink}
        rel="noopener noreferrer"
        sx={{ color: '#525252', textDecoration: 'underline' }}
        target="_blank"
        testID="dashboard-terms-and-conditions-link"
      >
        {t($ => $.app.footer.termsAndConditions)}
      </TextLink>
    </Stack>
  );
};
